import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSearch, FiClock, FiRefreshCw } from "react-icons/fi";
import "../assets/css/DataTable.css";
import {
  getCompanyData,
  searchUser,
  getAdminPanelCountsAPI,
} from "../Service/apiService";
import ColumnGroup from "antd/es/table/ColumnGroup";

// ====== COLUMN LABELS FOR ALL ======
const COLUMN_LABELS = {
  // === User columns ===
  companyName: "Company Name",
  name: "Name",
  email: "Email",
  mobile: "Mobile",
  role: "Role",
  designation: "Designation",
  industry: "Industry",
  activeStatus: "Status",
  subStartDate: "Subscription Start Date",
  subEndDate: "Subscription End Date",
  country: "Country Name",
  companyType: "Company Status",
  paymentStatus: "Payment Status",
  invoiceDate: "Paid Date",
  leadsDate: "Leads Date",
  demo: "Demo",
  demoDate: "Demo Date",
  personalMeeting: "Personal Meeting",
  personalMeetingDate: "Personal Meeting Date",
  userCreatedBy: "Created By",
  searchCount: "Search Count",
  oneWeekCount: "1 Week Count",
  oneMonthCount: "1 Month Count",
  threeMonthCount: "3 Month Count",
  sixMonthCount: "6 Month Count",
  twelveMonthCount: "12 Month Count",

  // === Company-specific columns ===
  _id: "Company Name",
  totalUsers: "Total Users",
  totalActiveUsers: "Active Users",
  subEndDate: "Expires On",
  companyStatus: "Status",
  overall: "Overall Count",
  oneMonthTotal: "1 Month Count",
  threeMonthTotal: "3 Month Count",
  sixMonthTotal: "6 Month Count",
  twelveMonthTotal: "12 Month Count",
  companyType: "Company Type",
  paymentStatus: "Payment Status",
  invoiceDate: "Paid Date",
  leadsDate: "Leads Date",
  demo: "Demo",
  demoDate: "Demo Date",
  personalMeeting: "Personal Meeting",
  personalMeetingDate: "Personal Meeting Date",
  userCreatedBy: "Created By",
  industry: "Industry",

  // === LEAD-SPECIFIC COLUMNS (NEW) ===
  // Total Unique Leads
  title: "Lead Type",
  fullName: "Full Name",
  countryName: "Country",
  createdAt: "Created Date",
  count: "Count",
  history: "History",
  
  // Free Trial Leads
  loginCount: "Login Count",
  loginHistory: "Login History",
  updatedAt: "Last Login Date",
  
  // Demo Leads
  companyName: "Company Name",
  country: "Country",
  mobileNumber: "Contact Number",
  demoRequestCount: "Demo Request Count",
  demoRequestHistory: "Demo Request History",
  demoDate: "Demo Date",
  
  // Pricing Leads
  contactNumber: "Contact Number",
  product: "Product",
  region: "Region",
  totalPrice: "Total Price",
  submissionCount: "Submission Count",
  submissionHistory: "Submission History",
  selections: "Selections",
  
  // HSN Leads
  hsnCode: "HS Code",
  avgPriceValue: "Average Price Value",
  hsCodeCount: "HS Code Count",
  hsCodeHistory: "HS Code History",
  
  // API Calculator Leads
  ksmData: "KSM Data",
  apiCalcCount: "API Calculator Count",
  apiCalcHistory: "API Calculator History",
  
  // Resource Leads
  reportName: "Report Name",
  downloadCount: "Download Count",
  downloadHistory: "Download History",
  
  // Subscribed Leads
  subscribeCount: "Subscribe Count",
  subscribeHistory: "Subscribe History",
  
  // Event Leads
  eventDate: "Event Date",
  eventName: "Event Name",
  phoneNumber: "Contact Number",
  eventCount: "Event Count",
  eventHistory: "Event History",
  
  // Fallback for any missing keys
  email: "Email",
};

// 🔁 CHANGED: Define fixed column order for companies
const COMPANY_COLUMN_KEYS = [
  "_id",
  "totalUsers",
  "totalActiveUsers",
  "subEndDate",
  "companyStatus",
  "overall",
  "oneMonthTotal",
  "threeMonthTotal",
  "sixMonthTotal",
  "twelveMonthTotal",
  "companyType",
  "paymentStatus",
  "invoiceDate",
  "leadsDate",
  "demo",
  "demoDate",
  "personalMeeting",
  "personalMeetingDate",
  "userCreatedBy",
  "industry",
  "country",
];

const toLower = (val) => (val || "").toLowerCase().trim();

const isNotInternal = (entity) =>
  entity.companyType && toLower(entity.companyType) !== "internal";

const parseCountField = (val) => {
  if (val == null) return 0;
  if (typeof val === "string" && val.startsWith(">")) return 6;
  return parseInt(val) || 0;
};

const flattenObject = (obj, prefix = "") => {
  const flattened = {};
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        Object.assign(flattened, flattenObject(value, newKey));
      } else if (Array.isArray(value)) {
        flattened[newKey] =
          value.length > 0
            ? typeof value[0] === "object"
              ? `[${value.length} items]`
              : value.join(", ")
            : "";
      } else {
        flattened[newKey] = value;
      }
    }
  }
  return flattened;
};

const getAllKeys = (data) => {
  const keys = new Set();
  data.forEach((item) => {
    const flattened = flattenObject(item);
    Object.keys(flattened).forEach((key) => keys.add(key));
  });
  return Array.from(keys).sort();
};

const getResponsivePageSize = () => {
  if (typeof window === "undefined") return 10;
  if (window.innerHeight > 900) return 20;
  if (window.innerHeight > 750) return 15;
  return 10;
};

// ====== MAIN COMPONENT ======
export default function DataTable() {
  const navigate = useNavigate();
  const { category, id } = useParams();
 

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyModalData, setHistoryModalData] = useState({});

  useEffect(() => {
    setPageSize(getResponsivePageSize());
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (category === "companies") {
        const companyRes = await getCompanyData("all");
        const companies = companyRes?.data || [];

        const nonInternal = companies.filter(
          (c) => c.companyType && toLower(c.companyType) !== "internal"
        );

        let filteredData = [];

        const isActive = (c) =>
          c.companyStatus && toLower(c.companyStatus) === "active";
        const isTrialType = (c) =>
          c.companyType && toLower(c.companyType) === "trial";
        const isNewType = (c) =>
          c.companyType && toLower(c.companyType) === "new";
        const isRenewalType = (c) =>
          c.companyType && toLower(c.companyType) === "renewal";
        const isLostType = (c) =>
          c.companyType && toLower(c.companyType) === "lost";
        const isPaid = (c) =>
          c.paymentStatus && toLower(c.paymentStatus) === "paid";

        switch (id) {
          case "totalCompanies":
            filteredData = nonInternal;
            break;
          case "newCompanies":
            filteredData = nonInternal.filter(
              (c) => isNewType(c) && c.invoiceDate && isActive(c)
            );
            break;
          case "renewedCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                isRenewalType(c) && isPaid(c) && isActive(c) && c.invoiceDate
            );
            break;
          case "lostCompanies":
            filteredData = nonInternal.filter((c) => isLostType(c));
            break;
          case "totalActiveCompanies":
            filteredData = nonInternal.filter(
              (c) => c.invoiceDate && isActive(c) && !isLostType(c)
            );
            break;
          case "upcomingRenewalsCompany":
            const now = new Date();
            const futureLimit = new Date();
            futureLimit.setMonth(now.getMonth() + 3);
            filteredData = nonInternal.filter(
              (c) =>
                isRenewalType(c) &&
                isActive(c) &&
                c.subEndDate &&
                new Date(c.subEndDate) >= now &&
                new Date(c.subEndDate) <= futureLimit
            );
            break;
          case "unpaidCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                isActive(c) &&
                c.paymentStatus &&
                toLower(c.paymentStatus) === "unpaid"
            );
            break;
          case "trialCompanies":
            const seen = new Set();
            filteredData = nonInternal.filter((c) => {
              if (!c.companyType || !c.paymentStatus) return false;
              const isTrial =
                toLower(c.companyType) === "trial" &&
                toLower(c.paymentStatus) === "trial" &&
                isActive(c);
              if (!isTrial || seen.has(c._id)) return false;
              seen.add(c._id);
              return true;
            });
            break;
          case "totalDemoCompanies":
            filteredData = nonInternal.filter(
              (c) => parseCountField(c.demo) > 0
            );
            break;
          case "renewalDemoCompanies":
            filteredData = nonInternal.filter(
              (c) => isRenewalType(c) && parseCountField(c.demo) > 0
            );
            break;
          case "newDemoCompanies":
            filteredData = nonInternal.filter(
              (c) => isNewType(c) && parseCountField(c.demo) > 0
            );
            break;
          case "personalMeetingCompanies":
            filteredData = nonInternal.filter(
              (c) => parseCountField(c.personalMeeting) > 0
            );
            break;
          case "indiaDemoCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                c.country &&
                toLower(c.country) === "india" &&
                parseCountField(c.demo) > 0
            );
            break;
          case "foreignDemoCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                c.country &&
                toLower(c.country) !== "india" &&
                parseCountField(c.demo) > 0
            );
            break;
          case "invoicedCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                c.invoiceDate &&
                c.invoiceDate !== "" &&
                c.invoiceDate !== "-" &&
                c.invoiceDate !== "null" &&
                c.invoiceDate !== "undefined"
            );
            break;
          case "indiainvoiceCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                c.invoiceDate &&
                c.invoiceDate !== "" &&
                c.invoiceDate !== "-" &&
                c.invoiceDate !== "null" &&
                c.invoiceDate !== "undefined" &&
                c.country &&
                toLower(c.country) === "india"
            );
            break;
          case "foreigninvoiceCompanies":
            filteredData = nonInternal.filter(
              (c) =>
                c.invoiceDate &&
                c.invoiceDate !== "" &&
                c.invoiceDate !== "-" &&
                c.invoiceDate !== "null" &&
                c.invoiceDate !== "undefined" &&
                c.country &&
                toLower(c.country) !== "india" &&
                c.country.trim() !== ""
            );
            break;
          case "leadDateCompanies":
            filteredData = nonInternal.filter((c) => c.leadsDate);
            break;
          default:
            filteredData = nonInternal;
        }

        // 🔁 CHANGED: Replace flattenObject with structured transform for companies
        const transformedCompanies = filteredData.map((company) => {
          // Prefer companyName, fallback to _id
          const companyName = company.companyName || company._id || "-";
          return {
            _id: companyName,
            totalUsers:
              company.totalUsers != null ? String(company.totalUsers) : "-",
            totalActiveUsers:
              company.totalActiveUsers != null
                ? String(company.totalActiveUsers)
                : "-",
            subEndDate: company.subEndDate || "-",
            companyStatus: company.companyStatus || "-",
            overall: company.overall != null ? String(company.overall) : "-",
            oneMonthTotal:
              company.oneMonthTotal != null
                ? String(company.oneMonthTotal)
                : "-",
            threeMonthTotal:
              company.threeMonthTotal != null
                ? String(company.threeMonthTotal)
                : "-",
            sixMonthTotal:
              company.sixMonthTotal != null
                ? String(company.sixMonthTotal)
                : "-",
            twelveMonthTotal:
              company.twelveMonthTotal != null
                ? String(company.twelveMonthTotal)
                : "-",
            companyType: company.companyType || "-",
            paymentStatus: company.paymentStatus || "-",
            invoiceDate: company.invoiceDate || "-",
            leadsDate: company.leadsDate || "-",
            demo: company.demo != null ? String(company.demo) : "-",
            demoDate: company.demoDate || "-",
            personalMeeting:
              company.personalMeeting != null
                ? String(company.personalMeeting)
                : "-",
            personalMeetingDate: company.personalMeetingDate || "-",
            userCreatedBy: company.userCreatedBy || "-",
            industry: Array.isArray(company.industry)
              ? company.industry.filter((i) => i).join(", ") || "-"
              : company.industry || "-",
            country: company.country || "-",
          };
        });
        setRows(transformedCompanies);
        setColumns(COMPANY_COLUMN_KEYS);
      } else if (category === "users") {
        const userRes = await searchUser("", { userDeleted: false });
        const users = userRes?.result || [];

        const nonInternal = users.filter(isNotInternal);

        let filteredData = [];
        const isActiveUser = (u) => u.activeStatus === true;
        const isTrialType = (u) =>
          u.companyType && toLower(u.companyType) === "trial";
        const isNewType = (u) =>
          u.companyType && toLower(u.companyType) === "new";
        const isRenewalType = (u) =>
          u.companyType && toLower(u.companyType) === "renewal";
        const isLostType = (u) =>
          u.companyType && u.companyType === "Lost";
        const isInternalType = (u) =>
          u.companyType && toLower(u.companyType) === "internal";
        const isPaidUser = (u) =>
          u.paymentStatus && toLower(u.paymentStatus) === "paid";
        const isUnpaidUser = (u) =>
          u.paymentStatus && toLower(u.paymentStatus) === "unpaid";

        switch (id) {
          case "totalSearchCount":
          case "totalUsers":
            filteredData = nonInternal;
            break;
          case "newUsers":
            filteredData = nonInternal.filter(
              (u) => isNewType(u) && u.invoiceDate && isActiveUser(u)
            );
            break;
          case "renewedUsers":
            filteredData = nonInternal.filter(
              (u) =>
                isRenewalType(u) &&
                isPaidUser(u) &&
                isActiveUser(u) &&
                u.invoiceDate
            );
            break;
          case "lostUsers":
            filteredData = nonInternal.filter((u) => isLostType(u));
            break;
          case "activeUsers":
            filteredData = nonInternal.filter(
              (u) => u.invoiceDate && isActiveUser(u)
            );
            break;
          case "unpaidUsers":
            filteredData = nonInternal.filter(
              (u) => isActiveUser(u) && isUnpaidUser(u)
            );
            break;
          case "trialUsers": {
            const seen = new Set();
            filteredData = nonInternal.filter((u) => {
              if (
                !isActiveUser(u) ||
                !isTrialType(u) ||
                !u.paymentStatus ||
                toLower(u.paymentStatus) !== "trial"
              )
                return false;
              if (seen.has(u.email)) return false;
              seen.add(u.email);
              return true;
            });
            break;
          }
          case "internalUsers":
            filteredData = users.filter((u) => isInternalType(u));
            break;
          case "upcomingUsers": {
            const now = new Date();
            const futureLimit = new Date();
            futureLimit.setMonth(now.getMonth() + 3);
            filteredData = nonInternal.filter(
              (u) =>
                isRenewalType(u) &&
                isActiveUser(u) &&
                u.subEndDate &&
                new Date(u.subEndDate) >= now &&
                new Date(u.subEndDate) <= futureLimit
            );
            break;
          }
          case "mostActiveUsers":
            filteredData = nonInternal
              .filter((u) => isActiveUser(u))
              .sort((a, b) => (b.searchCount || 0) - (a.searchCount || 0));
            break;
          case "leastActiveUsers":
            filteredData = nonInternal
              .filter((u) => isActiveUser(u))
              .sort((a, b) => (a.searchCount || 0) - (b.searchCount || 0));
            break;
          case "inactiveUsers":
            filteredData = nonInternal.filter((u) => u.activeStatus === false);
            break;
          default:
            filteredData = [];
        }

        const transformedUsers = filteredData.map((user) => {
          const sc = user.searchCounts || {};
          return {
            companyName: user.companyName || "-",
            name: user.name || "-",
            email: user.email || "-",
            mobile: user.mobile != null ? String(user.mobile) : "-",
            role: user.role || "-",
            designation: user.designation || "-",
            industry: Array.isArray(user.industry)
              ? user.industry.filter((i) => i).join(", ") || "-"
              : user.industry || "-",
            activeStatus: user.activeStatus,
            companyType: user.companyType || "-",
            subStartDate: user.subStartDate || "-",
            subEndDate: user.subEndDate || "-",
            country: user.country || "-",
            paymentStatus: user.paymentStatus || "-",
            invoiceDate: user.invoiceDate || "-",
            leadsDate: user.leadsDate || "-",
            demo: user.demo != null ? String(user.demo) : "-",
            demoDate: user.demoDate || "-",
            personalMeeting:
              user.personalMeeting != null ? String(user.personalMeeting) : "-",
            personalMeetingDate: user.personalMeetingDate || "-",
            userCreatedBy: user.userCreatedBy || "-",
            searchCount:
              user.searchCount != null ? String(user.searchCount) : "-",
            oneWeekCount:
              sc.oneWeekCount != null ? String(sc.oneWeekCount) : "-",
            oneMonthCount:
              sc.oneMonthCount != null ? String(sc.oneMonthCount) : "-",
            threeMonthCount:
              sc.threeMonthCount != null ? String(sc.threeMonthCount) : "-",
            sixMonthCount:
              sc.sixMonthCount != null ? String(sc.sixMonthCount) : "-",
            twelveMonthCount:
              sc.twelveMonthCount != null ? String(sc.twelveMonthCount) : "-",
          };
        });

        const USER_COLUMN_KEYS = [
          "companyName",
          "name",
          "email",
          "mobile",
          "role",
          "designation",
          "industry",
          "activeStatus",
          "subStartDate",
          "subEndDate",
          "country",
          "companyType",
          "paymentStatus",
          "invoiceDate",
          "leadsDate",
          "demo",
          "demoDate",
          "personalMeeting",
          "personalMeetingDate",
          "userCreatedBy",
          "searchCount",
          "oneWeekCount",
          "oneMonthCount",
          "threeMonthCount",
          "sixMonthCount",
          "twelveMonthCount",
        ];

        setRows(transformedUsers);
        setColumns(USER_COLUMN_KEYS);
     } else if (category === "leads") {
  let leadsRes;
  try {
    leadsRes = await getAdminPanelCountsAPI({});
  } catch (err) {
    console.error("Error fetching leads data:", err);
    leadsRes = { result: {} };
  }

  const leadsStats = leadsRes?.result || {};

  
  if (id === "total") {
    const allLeads = [
      ...(leadsStats.freeTrialUserData || []).map((item) => ({
        ...item,
        leadType: "Free Trial",
      })),
      ...(leadsStats.demoUserData || []).map((item) => ({
        ...item,
        leadType: "Demo",
      })),
      ...(leadsStats.pricingUserData || []).map((item) => ({
        ...item,
        leadType: "Pricing",
      })),
      ...(leadsStats.hsnUserData || []).map((item) => ({
        ...item,
        leadType: "HSN Price",
      })),
      ...(leadsStats.apiCalculatorUserData || []).map((item) => ({
        ...item,
        leadType: "API Calculator",
      })),
      ...(leadsStats.resourceUserData || []).map((item) => ({
        ...item,
        leadType: "Resource",
      })),
      ...(leadsStats.subscribedUserData || []).map((item) => ({
        ...item,
        leadType: "Email Subscriber",
      })),
      ...(leadsStats.eventData || []).map((item) => ({
        ...item,
        leadType: "Event",
      })),
    ];

    const normalizedLeads = allLeads.map((item, idx) => {
      const fullName =
        item.fullName ||
        item.name ||
        (item.companyName ? `${item.companyName} (Org)` : "-");

      const email = item.email || "-";
      const country = item.country || item.countryName || "-";
      let date =
        item.createdAt ||
        item.updatedAt ||
        item.demoDate ||
        item.eventDate ||
        "-";
      const count =
        item.loginCount ||
        item.demoRequestCount ||
        item.pricingSubmissionCount ||
        item.hsCodeCount ||
        item.apiCalcCount ||
        item.downloadCount ||
        item.subscribeCount ||
        item.eventCount ||
        1;

      return {
        leadType: item.leadType || "-",
        fullName,
        email,
        country,
        createdAt: date,
        count: String(count),
      };
    });

    const totalLeadsColumns = [
      "leadType",
      "fullName",
      "email",
      "country",
      "createdAt",
      "count",
    ];

    setColumns(totalLeadsColumns);
    setRows(normalizedLeads);
    setLoading(false);
    return; // ⚠️ Important: skip rest of logic
  }

  //  For all other lead types, use existing logic
  const getHeadersAndFormattedData = (id, data) => {
    let customHeaders = {};
    let formattedData = [];

    switch (id) {
      case "totalUniqueLeads":
        customHeaders = {
          title: "Lead Type",
          email: "Email",
          createdAt: "Created Date",
          countryName: "Country Name",
          count: "Count",
          history: "History",
        };
        break;
      case "freeTrialUserData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          updatedAt: "Last Login",
          loginCount: "Login Count",
          loginHistory: "History",
        };
        break;
      case "demoUserData":
        customHeaders = {
          fullName: "Name",
          companyName: "Company Name",
          country: "Country",
          email: "Email",
          mobileNumber: "Contact Number",
          updatedAt: "Demo Date",
          demoRequestCount: "Count",
          demoRequestHistory: "History",
        };
        break;
      case "pricingUserData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          contactNumber: "Contact Number",
          companyName: "Company Name",
          countryName: "Country",
          product: "Product",
          region: "Region",
          totalPrice: "Total Price",
          createdAt: "Created Date",
          submissionCount: "Count",
          submissionHistory: "History",
        };
        break;
      case "hsnUserData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          hsnCode: "HS Code",
          avgPriceValue: "Avg. Price Value",
          createdAt: "Created Date",
          hsCodeCount: "Count",
          hsCodeHistory: "History",
        };
        break;
      case "apiCalculatorUserData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          ksmData: "ksm Data",
          avgPriceValue: "Avg. Price Value",
          createdAt: "Created Date",
          apiCalcCount: "Count",
          apiCalcHistory: "History",
        };
        break;
      case "resourceUserData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          reportName: "Report Name",
          createdAt: "Created Date",
          downloadCount: "Count",
          downloadHistory: "History",
        };
        break;
      case "subscribedUserData":
        customHeaders = {
          email: "Email",
          createdAt: "Created Date",
          subscribeCount: "Count",
          subscribeHistory: "History",
        };
        break;
      case "eventData":
        customHeaders = {
          fullName: "Name",
          email: "Email",
          eventDate: "Event Date",
          eventName: "Event Name",
          createdAt: "Created Date",
          phoneNumber: "Contact Number",
          eventCount: "Count",
          eventHistory: "History",
        };
        break;
      default:
        customHeaders = data[0]
          ? Object.keys(data[0]).reduce((acc, key) => {
              acc[key] = key;
              return acc;
            }, {})
          : {};
    }

    const formatTotalUniqueLeads = (items) => {
      return items.map((item) => {
        const rawCountry = item.countryName || item.country || "-";
        const row = {
          title: item.title || "-",
          email: item.email || "-",
          createdAt: item.createdAt || "-",
          countryName: rawCountry.toString().toUpperCase(),
          count: "-",
          history: "-",
        };

        const countFields = [
          "freeTrialCount",
          "demoRequestCount",
          "pricingSubmissionCount",
          "hsCodeCount",
          "apiCalcCount",
          "downloadCount",
          "subscribeCount",
          "eventCount",
        ];
        for (const field of countFields) {
          if (item[field] !== undefined) {
            row.count = item[field];
            break;
          }
        }

        const historyMap = {
          loginHistory: "loginHistory",
          demoRequestHistory: "demoRequestHistory",
          submissionHistory: "submissionHistory",
          hsCodeHistory: "hsCodeHistory",
          apiCalcHistory: "apiCalcHistory",
          downloadHistory: "downloadHistory",
          subscribeHistory: "subscribeHistory",
          eventHistory: "eventHistory",
        };
        for (const [key, val] of Object.entries(historyMap)) {
          if (item[key] && Array.isArray(item[key])) {
            row.history = item[key].map((d) => ({ timestamp: d }));
            break;
          }
        }
        return row;
      });
    };

    const formatOtherLeads = (items) => {
      return items.map((item) => {
        const row = {};
        Object.keys(customHeaders).forEach((key) => {
          if (
            key.toLowerCase().includes("history") &&
            Array.isArray(item[key])
          ) {
            row[key] = item[key].map((d) => ({ timestamp: d }));
          } else if (key === "product") {
            row[key] =
              item?.selections?.product?.map((p) => `> ${p}`).join("\n") || "-";
          } else if (key === "region") {
            row[key] = item?.selections?.region || "-";
          } else {
            row[key] = item[key] != null ? item[key] : "-";
          }
        });
        return row;
      });
    };

    if (id === "totalUniqueLeads") {
      formattedData = formatTotalUniqueLeads(data);
    } else {
      formattedData = formatOtherLeads(data);
    }

    return { customHeaders, formattedData };
  };

  let rawData = [];
  switch (id) {
    case "totalUniqueLeads":
      rawData = leadsStats.totalUniqueLeads || [];
      break;
    case "freeTrialUserData":
      rawData = leadsStats.freeTrialUserData || [];
      break;
    case "demoUserData":
      rawData = leadsStats.demoUserData || [];
      break;
    case "pricingUserData":
      rawData = leadsStats.pricingUserData || [];
      break;
    case "hsnUserData":
      rawData = leadsStats.hsnUserData || [];
      break;
    case "apiCalculatorUserData":
      rawData = leadsStats.apiCalculatorUserData || [];
      break;
    case "resourceUserData":
      rawData = leadsStats.resourceUserData || [];
      break;
    case "subscribedUserData":
      rawData = leadsStats.subscribedUserData || [];
      break;
    case "eventData":
      rawData = leadsStats.eventData || [];
      break;
    default:
      rawData = [];
  }

  const { customHeaders, formattedData } = getHeadersAndFormattedData(id, rawData);
  const columnKeys = Object.keys(customHeaders);
  setColumns(columnKeys);
  setRows(formattedData);
}
    } catch (error) {
      console.error("Error fetching data:", error);
      setRows([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [category, id]);

  // ====== TABLE LOGIC ======
  const totalPages = Math.ceil(rows.length / pageSize);

const sortedRows = useMemo(() => {
  if (!sortConfig.key || !sortConfig.direction) {
    return rows; //  default order
  }

  return [...rows].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === "asc"
      ? aValue > bValue ? 1 : -1
      : aValue < bValue ? 1 : -1;
  });
}, [rows, sortConfig]);


  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    return sortedRows.filter((row) =>
      Object.values(row).some(
        (value) =>
          value != null &&
          String(value).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [sortedRows, search]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [page, pageSize, filteredRows]);

  const goToNext = () => page < totalPages && setPage(page + 1);
  const goToPrev = () => page > 1 && setPage(page - 1);

  const requestSort = (key) => {
  if (sortConfig.key !== key) {
    // First click → ascending
    setSortConfig({ key, direction: "asc" });
  } else if (sortConfig.direction === "asc") {
    // Second click → descending
    setSortConfig({ key, direction: "desc" });
  } else if (sortConfig.direction === "desc") {
    // Third click → reset (no sorting)
    setSortConfig({ key: null, direction: null });
  }
};


const getSortIcon = (columnKey) => {
  if (sortConfig.key !== columnKey || !sortConfig.direction) {
    return <span className="sort-icon">↑↓</span>; // default
  }
  return sortConfig.direction === "asc" ? "↑" : "↓";
};


  const renderCell = (value, columnKey, row = {}, currentCategory = "") => {
    // Standard null/empty handling
    if (value == null || value === "" || value === "-") return "-";

    // 🔹 ONLY for "leads" category: handle history columns with icon
    if (
      currentCategory === "leads" &&
      columnKey.toLowerCase().includes("history") &&
      Array.isArray(value)
    ) {
      if (value.length === 0) return "-";
      return (
        <span
          style={{ cursor: "pointer", color: "#007bff" }}
          onClick={(e) => {
            e.stopPropagation(); // prevent parent row click
            setHistoryModalData({
              email: row.email || row.Email || "N/A",
              history: value,
            });
            setShowHistoryModal(true);
          }}
        >
          <FiClock title="View History" style={{ verticalAlign: "-0.125em" }} />
        </span>
      );
    }

    // Standard formatting for other columns
    if (columnKey === "activeStatus") {
      return value === true ? "Active" : "Inactive";
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-GB");
      }
    }

    // Handle multi-line (e.g., product)
    if (typeof value === "string" && value.includes("\n")) {
      return value.split("\n").map((line, i) => (
        <React.Fragment key={i}>
          {line}
          <br />
        </React.Fragment>
      ));
    }

    return String(value);
  };
  if (loading) {
    return (
      <div className="company-table-page">
        <div className="header-row">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft size={16} /> Back
          </button>
        </div>

        <div className="table-wrapper">
          <table className="custom-table">
            <thead>
              <tr className="skeleton-header">
                <th>Sr. no</th>
                {[...Array(8)].map((_, i) => (
                  <th key={i}>
                    <div className="skeleton-cell" />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {[...Array(10)].map((_, rowIndex) => (
                <tr key={rowIndex} className="skeleton-row">
                  <td>
                    <div className="skeleton-cell" />
                  </td>

                  {[...Array(8)].map((_, cellIndex) => (
                    <td key={cellIndex}>
                      <div className="skeleton-cell" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="company-table-page">
      <div className="header-row">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} /> Back
        </button>
        <div className="actions">
          <div className="search-box">
            <FiSearch className="icon" />

            <input
              type="text"
              placeholder="Search all columns..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />

            {search && (
              <FiRefreshCw
                className="refresh-icon"
                title="Clear search"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Sr. no</th>
              {columns.map((key, i) => (
                <th
                  key={i}
                  onClick={() => requestSort(key)}
                  className="sortable"
                >
                  {COLUMN_LABELS[key] || key}
                  <span className="sort-indicator">{getSortIcon(key)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{(page - 1) * pageSize + rowIndex + 1}</td>
                  {columns.map((col, colIndex) => (
                    <td key={colIndex}>
                      {renderCell(row[col], col, row, category)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-data">
                  💤 No data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button onClick={goToPrev} disabled={page === 1}>
          Prev
        </button>
        <span>
          Page {page} / {totalPages || 1}
        </span>
        <button onClick={goToNext} disabled={page >= totalPages}>
          Next
        </button>
      </div>
      {/* History Modal*/}
      {showHistoryModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowHistoryModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">History</h5>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowHistoryModal(false)}
              >
                &times;
              </button>
            </div>

            {historyModalData?.history?.length > 0 ? (
              <table className="modal-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Email</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {historyModalData.history.map((entry, idx) => {
                    const ts = entry?.timestamp || entry;
                    const date = new Date(ts);
                    const formattedDate = isNaN(date.getTime())
                      ? "-"
                      : date.toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        });
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{historyModalData.email || "N/A"}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="no-history-message">No history data found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
